/**
 * BettingContextStrip - ATS and O/U records as horizontal stacked bars (Tufte-style).
 *
 * Sport-agnostic: accepts array of {label, wins, losses, pushes} records.
 *
 * @param {Array} records - [{label, wins, losses, pushes, awayWins, awayLosses, awayPushes}]
 * @param {Object} homeTeam - {abbreviation}
 * @param {Object} awayTeam - {abbreviation}
 */
import Card from '../../common/Card';

const RecordBar = ({ label, wins, losses, pushes, teamColor }) => {
  const total = (wins || 0) + (losses || 0) + (pushes || 0);
  if (total === 0) return null;

  const winPct = (wins / total) * 100;
  const lossPct = (losses / total) * 100;
  const pushPct = (pushes / total) * 100;

  return (
    <div className="space-y-0.5">
      <div className="flex items-center justify-between">
        <span className="text-[9px] text-text-secondary">{label}</span>
        <span className="font-mono text-[9px] text-text-secondary">
          {wins}-{losses}{pushes > 0 ? `-${pushes}` : ''}
        </span>
      </div>
      <div className="flex h-2 rounded-sm overflow-hidden">
        {winPct > 0 && (
          <div
            className={`${teamColor === 'home' ? 'bg-accent-blue' : 'bg-purple-400'}`}
            style={{ width: `${winPct}%` }}
          />
        )}
        {pushPct > 0 && (
          <div
            className="bg-gray-500/40"
            style={{ width: `${pushPct}%` }}
          />
        )}
        {lossPct > 0 && (
          <div
            className="bg-danger/50"
            style={{ width: `${lossPct}%` }}
          />
        )}
      </div>
    </div>
  );
};

const BettingContextStrip = ({
  records = [],
  homeTeam = {},
  awayTeam = {},
}) => {
  if (records.length === 0) return null;

  // Check if any record has actual data to show
  const hasData = records.some(rec => {
    const homeTotal = (rec.wins || 0) + (rec.losses || 0) + (rec.pushes || 0);
    const awayTotal = (rec.awayWins || 0) + (rec.awayLosses || 0) + (rec.awayPushes || 0);
    return homeTotal > 0 || awayTotal > 0;
  });
  if (!hasData) return null;

  return (
    <Card className="p-3">
      <h3 className="text-xs font-semibold text-text-secondary uppercase tracking-wider mb-2">
        Betting Context
      </h3>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {records.map((rec, i) => (
          <div key={i}>
            <div className="text-[10px] font-semibold text-text-primary mb-1">{rec.label}</div>

            {/* Away team bar */}
            <div className="mb-1.5">
              <RecordBar
                label={awayTeam.abbreviation || 'Away'}
                wins={rec.awayWins}
                losses={rec.awayLosses}
                pushes={rec.awayPushes}
                teamColor="away"
              />
            </div>

            {/* Home team bar */}
            <RecordBar
              label={homeTeam.abbreviation || 'Home'}
              wins={rec.wins}
              losses={rec.losses}
              pushes={rec.pushes}
              teamColor="home"
            />
          </div>
        ))}
      </div>
    </Card>
  );
};

export default BettingContextStrip;
